import React, { Fragment } from 'react'
import {
  Typography, SpacerHorizontal, SpacerVertical
} from '@klarna/bubble-ui'
import { Node } from '../types'
import getValueFromNodes from '../utilities/getValueFromNode'
import PaymentStepsTimeline from '../components/PaymentStepsTimeline/PaymentStepsTimeline'
import config from '../config'
import LegalText from '../components/LegalText/LegalText'
import PaymentMethodContainer from './PaymentMetodContainer/PaymentMethodContainer'
import { isAmountGreaterThanZero, isValidAmount } from '../utilities/amountUtils'
import RiskDisclaimer from '../components/RiskDisclaimer/RiskDeclaimer'

type PaymentCalculatorProps = {
  nodes: Node[]
  theme: string
};

const PaymentCalculator: React.FC<PaymentCalculatorProps> = ({ nodes, theme }) => {
  const backgroundColorKey = theme === 'dark' ? 'darkBackgroundTheme' : 'backgroundTheme'
  const { frame_component: frameBackground } = config[backgroundColorKey]
  const fiscInstallments = nodes.filter(node => node.name.indexOf('FIXED_SUM_CREDIT_BADGE') > -1).map(node => node['no_of_installments'])
  const getAprTextForFISCTier = (riskBasedPricing: string, apr: string, overallMinApr: string, overallMaxApr: string) => {
    if (riskBasedPricing === 'true') {
      return (overallMinApr === overallMaxApr) ? `${overallMinApr}%` : `${overallMinApr}% - ${overallMaxApr}%`
    }
    return `${apr}%`
  }

  const payNowProps = {
    badge: (getValueFromNodes(nodes, 'PAY_BY_CARD_BADGE')
    || getValueFromNodes(nodes, 'CARD_BADGE')
    || getValueFromNodes(nodes, 'DIRECT_DEBIT_BADGE')
    || getValueFromNodes(nodes, 'BANK_TRANSFER_CARD_BADGE')),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: (getValueFromNodes(nodes, 'PAY_BY_CARD_FREQUENCY_TITLE')
    || getValueFromNodes(nodes, 'CARD_FREQUENCY_TITLE')
    || getValueFromNodes(nodes, 'DIRECT_DEBIT_FREQUENCY_TITLE')
    || getValueFromNodes(nodes, 'BANK_TRANSFER_FREQUENCY_TITLE')),
    backgroundColorKey,
  }

  const payNowLegalText = getValueFromNodes(nodes, 'PAY_BY_CARD_LEGAL_TEXT')
  || getValueFromNodes(nodes, 'CARD_LEGAL_TEXT')
  || getValueFromNodes(nodes, 'DIRECT_DEBIT_LEGAL_TEXT')
  || getValueFromNodes(nodes, 'BANK_TRANSFER_LEGAL_TEXT')

  const invoiceProps = {
    badge: getValueFromNodes(nodes, 'INVOICE_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: getValueFromNodes(nodes, 'INVOICE_FREQUENCY_TITLE'),
    backgroundColorKey,
  }

  const payLaterByCardProps = {
    badge: getValueFromNodes(nodes, 'PAY_LATER_BY_CARD_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: getValueFromNodes(nodes, 'PAY_LATER_BY_CARD_FREQUENCY_TITLE'),
    backgroundColorKey,
  }

  const sibcProps = {
    badge: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BADGE'),
    frequencySteps: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_FREQUENCY_STEPS'),
    frequencyTitle: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_FREQUENCY_TITLE'),
    breakdown: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BREAKDOWN'),
    installmentAmount: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INSTALLMENT_AMOUNT'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    backgroundColorKey,
  }

  const interestAmount = getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INTEREST_AMOUNT')
  const sibcDetailsProps = {
    installmentsTotal: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
    interestTranslation: getValueFromNodes(nodes, 'INTEREST_TRANSLATION'),
    interestFreeTranslation: getValueFromNodes(nodes, 'INTEREST_FREE_TRANSLATION'),
    interestAmount: isValidAmount(interestAmount) ? interestAmount : null,
    isInterestGreaterThanZero: isAmountGreaterThanZero(interestAmount),
    apr: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR'),
    aprParagraph: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR_PARAGRAPH'),
    aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION'),
  }

  const fiscProps: any = {}
  const fiscDetailsProps: any = {}

  fiscInstallments.forEach(installment => {
    const downPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_DOWNPAYMENT_${installment}`)
    const finalPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_FINALPAYMENT_${installment}`)
    const ballonPercentage = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BALOON_PERCENTAGE_${installment}`)
    const aprText = getAprTextForFISCTier(
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_WITH_RISKBASED_PRICING'),
      getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`),
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MIN_APR'),
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MAX_APR')
    )

    fiscProps[installment] = {
      badge: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BADGE_${installment}`),
      purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
      frequencyTitle: getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_FREQUENCY_TITLE'),
      installmentAmount: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_INSTALLMENT_MONTHLY_${installment}`),
      backgroundColorKey,
    }

    fiscDetailsProps[installment] = {
      totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
      apr: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`),
      aprParagraph: aprText,
      aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION'),
      downPayment: isAmountGreaterThanZero(downPayment) ? downPayment : null,
      downPaymentTranslation: getValueFromNodes(nodes, 'DOWN_PAYMENT_TRANSLATION'),
      finalPayment: isAmountGreaterThanZero(ballonPercentage) ? finalPayment : null,
      finalPaymentTranslation: getValueFromNodes(nodes, 'FINAL_PAYMENT_TRANSLATION'),
      installmentsTotal: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_TOTAL_AMOUNT_${installment}`),
    }
  })

  return (
    <Fragment>
      <Typography
        accessibilityPreset='heading 1'
        textToken="text-style/headings/titles/bold/tertiary"
        textAlignToken="text-alignment/start"
      >
        {getValueFromNodes(nodes, 'TITLE')}
      </Typography>
      <SpacerVertical spaceToken="space.100" />

      <Typography
        accessibilityPreset='heading 2'
        textToken="text-style/text/super-paragraphs/default/regular"
        colorToken="colors/text/subtle"
        textAlignToken="text-alignment/start"
      >
        {getValueFromNodes(nodes, 'SUB_TITLE')}
      </Typography>
      <SpacerVertical spaceToken="space.400" />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        legalText={payNowLegalText}
        paymentMethodProps={payNowProps}/>

      <PaymentMethodContainer
        frameBackground={frameBackground}
        legalText={getValueFromNodes(nodes, 'INVOICE_LEGAL_TEXT')}
        paymentMethodProps={invoiceProps} />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        legalText={getValueFromNodes(nodes, 'PAY_LATER_BY_CARD_LEGAL_TEXT')}
        paymentMethodProps={payLaterByCardProps} />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        legalText={getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_LEGAL_TEXT')}
        paymentMethodWithProgressProps={sibcProps}
        paymentMethodDetailsProps={sibcDetailsProps}/>

      {fiscInstallments.map(installment => (
        <PaymentMethodContainer
          frameBackground={frameBackground}
          legalText={''}
          paymentMethodProps={fiscProps[installment]}
          paymentMethodDetailsProps={fiscDetailsProps[installment]}/>
      ))}

      <LegalText
        legalText={getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_LEGAL_TEXT')}
      />

      <PaymentStepsTimeline
        nodes={nodes}
        frameBackground={frameBackground}
      />

      <RiskDisclaimer
        frameBackground={frameBackground}
        riskDesclaimerHeading={getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_RISK_DISCLAIMER_HEADING') || ''}
        riskDisclaimerSubHeading={getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SUBHEADING') || ''}
        riskDesclaimerSource={getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SOURCE') || ''} />
      <SpacerHorizontal spaceToken="space.400" />
    </Fragment>
  )
}

export default PaymentCalculator
